import { useMemo, useState } from "react";
import { Search, PackageCheck, Truck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

export const paymentPill = (status, method) => {
  if (status === "cancelled") return { label: "Cancelled", cls: "bg-red-100 text-red-800 border-red-200" };
  if (method === "cod") {
    return status === "delivered"
      ? { label: "COD · Collected", cls: "bg-green-100 text-green-800 border-green-200" }
      : { label: "COD", cls: "bg-orange-100 text-orange-800 border-orange-200" };
  }
  if (status === "pending") return { label: "Payment pending", cls: "bg-amber-100 text-amber-800 border-amber-200" };
  return { label: method === "wallet" ? "Prepaid · Wallet" : "Prepaid · Card", cls: "bg-green-100 text-green-800 border-green-200" };
};

export const fulfillmentPill = (status) => {
  if (status === "delivered") return { label: "Fulfilled", cls: "bg-gray-800 text-white border-gray-800" };
  if (status === "cancelled") return { label: "—", cls: "bg-gray-100 text-gray-400 border-gray-200" };
  if (status === "returned") return { label: "Returned", cls: "bg-purple-100 text-purple-800 border-purple-200" };
  if (status === "awaiting_confirmation") return { label: "COD unconfirmed", cls: "bg-amber-100 text-amber-800 border-amber-200" };
  if (["packed", "shipped", "out_for_delivery"].includes(status)) return { label: "In transit", cls: "bg-blue-100 text-blue-800 border-blue-200" };
  return { label: "Unfulfilled", cls: "bg-amber-100 text-amber-800 border-amber-200" };
};

const FILTERS = [
  { key: "all", label: "All" },
  { key: "unfulfilled", label: "Unfulfilled" },
  { key: "in_transit", label: "In transit" },
  { key: "delivered", label: "Delivered" },
  { key: "returned", label: "Returned" },
  { key: "cancelled", label: "Cancelled" },
];

const matches = (o, f) => {
  if (f === "all") return true;
  if (f === "unfulfilled") return ["pending", "awaiting_confirmation", "confirmed"].includes(o.status);
  if (f === "in_transit") return ["packed", "shipped", "out_for_delivery"].includes(o.status);
  return o.status === f;
};

export const OrdersTab = ({ orders, onSelect, onSaved }) => {
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return orders.filter((o) =>
      matches(o, filter) &&
      (!q || o.id.toLowerCase().includes(q) || (o.customer_email || "").toLowerCase().includes(q) || (o.customer_name || "").toLowerCase().includes(q) || (o.customer_phone || "").includes(q))
    );
  }, [orders, filter, query]);

  const selectable = filtered.filter((o) => !["delivered", "cancelled", "returned"].includes(o.status));
  const allSelected = selectable.length > 0 && selectable.every((o) => selected.includes(o.id));

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleAll = () => setSelected(allSelected ? [] : selectable.map((o) => o.id));

  const bulkUpdate = async (status) => {
    setBusy(true);
    try {
      const results = await Promise.allSettled(selected.map((id) => api.put(`/admin/orders/${id}`, { status })));
      const ok = results.filter((r) => r.status === "fulfilled").length;
      toast.success(`${ok} order${ok === 1 ? "" : "s"} marked ${status.replace(/_/g, " ")}`);
      const failed = results.find((r) => r.status === "rejected");
      if (failed) toast.error(formatApiErrorDetail(failed.reason?.response?.data?.detail));
      setSelected([]);
      onSaved?.();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-4" data-testid="orders-tab">
      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            data-testid={`orders-filter-${f.key}`}
            className={`px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors border ${
              filter === f.key ? "bg-gray-900 text-white border-gray-900" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
            }`}
          >
            {f.label} <span className="text-xs opacity-70">({orders.filter((o) => matches(o, f.key)).length})</span>
          </button>
        ))}
        <div className="relative ml-auto w-full sm:w-72">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search order, customer…"
            data-testid="orders-search-input"
            className="w-full bg-white border border-gray-300 rounded-lg text-sm pl-9 pr-3 py-2 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#E80071]/30 focus:border-[#E80071]"
          />
        </div>
      </div>

      {selected.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 bg-white border border-[#E80071]/30 rounded-xl shadow-sm px-4 py-3" data-testid="bulk-actions-bar">
          <span className="text-sm text-gray-700 font-medium">{selected.length} selected</span>
          <button onClick={() => bulkUpdate("packed")} disabled={busy} data-testid="bulk-pack-btn" className="flex items-center gap-2 bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors shadow-sm disabled:opacity-50">
            <PackageCheck size={14} /> Mark packed
          </button>
          <button onClick={() => bulkUpdate("shipped")} disabled={busy} data-testid="bulk-ship-btn" className="flex items-center gap-2 bg-[#E80071] hover:bg-[#D00065] text-white px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors shadow-sm disabled:opacity-50">
            <Truck size={14} /> Mark shipped
          </button>
          <button onClick={() => setSelected([])} data-testid="bulk-clear-btn" className="text-sm text-gray-400 hover:text-gray-600 ml-auto">Clear</button>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-x-auto" data-testid="orders-table-card">
        <table className="w-full text-left border-collapse min-w-[820px]">
          <thead>
            <tr>
              <th className="bg-gray-50 py-3 px-4 border-b border-gray-200 w-10">
                <input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="orders-select-all" className="accent-[#E80071] w-4 h-4" aria-label="Select all" />
              </th>
              {["Order", "Date", "Customer", "Payment", "Fulfillment", "Items", "Total"].map((h) => (
                <th key={h} className="bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wider py-3 px-4 border-b border-gray-200">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={8} className="py-14 text-center text-gray-400 text-sm" data-testid="orders-empty">No orders match this view.</td></tr>
            ) : (
              filtered.map((o) => {
                const pay = paymentPill(o.status, o.payment_method);
                const ful = fulfillmentPill(o.status);
                const canSelect = !["delivered", "cancelled", "returned"].includes(o.status);
                return (
                  <tr key={o.id} onClick={() => onSelect(o)} className="hover:bg-gray-50 cursor-pointer transition-colors" data-testid={`order-row-${o.id}`}>
                    <td className="py-3 px-4 border-b border-gray-100" onClick={(e) => e.stopPropagation()}>
                      {canSelect && (
                        <input type="checkbox" checked={selected.includes(o.id)} onChange={() => toggle(o.id)} data-testid={`order-check-${o.id}`} className="accent-[#E80071] w-4 h-4" aria-label="Select order" />
                      )}
                    </td>
                    <td className="py-3 px-4 border-b border-gray-100 font-medium text-[#E80071]">#{o.id.slice(0, 8).toUpperCase()}</td>
                    <td className="py-3 px-4 border-b border-gray-100 text-gray-600 text-sm">{new Date(o.created_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</td>
                    <td className="py-3 px-4 border-b border-gray-100 text-gray-700 text-sm">{o.customer_name || o.customer_email || "Guest"}</td>
                    <td className="py-3 px-4 border-b border-gray-100"><span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${pay.cls}`} data-testid={`order-payment-${o.id}`}>{pay.label}</span></td>
                    <td className="py-3 px-4 border-b border-gray-100"><span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${ful.cls}`} data-testid={`order-fulfillment-${o.id}`}>{ful.label}</span></td>
                    <td className="py-3 px-4 border-b border-gray-100 text-gray-600 text-sm">{o.items.reduce((s, i) => s + i.quantity, 0)}</td>
                    <td className="py-3 px-4 border-b border-gray-100 font-medium text-gray-900">₹{o.total.toLocaleString("en-IN")}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
